import { useLocaleFormatters } from "$root/localization/hooks";
import { PaletteColors } from "$root/styles/themePalette";
import {
	Highcharts,
	useHighchartsDiscretizedZoom,
	useHighchartsReapplyButtonState,
} from "$root/third-party-integrations/highcharts-with-modules";
import { getThemeCssVars, stableColorGenerator } from "$root/utils";
import { Column } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useQuery } from "@tanstack/react-query";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import PortfolioPerformanceWorker from "./portfolio-performance.worker?worker";
import type { PortfolioPerformanceWorkerApi } from "./portfolio-performance.worker";
import { wrapTerminableWorker } from "./terminable-worker";
import { workerId } from "./worker-id";
import type { ColumnState } from "ag-grid-community";
import { useRefProxy } from "@mdotm/shadcn/legacy/react-extensions";
import { useDisposable } from "@mdotm/utils/react-extra";
import type { InvestmentSummaryWithRequiredUuid } from "$root/pages/PortfolioDetails/common";
import {
	adjustDateRangeSkippingWeekends,
	type ComputedMetrics,
	computeDateRangeWholeSeries,
	type PerformancePanelRow,
	type VisibleSeries,
} from "./common";
import {
	type ColumnPersistence,
	type ControlledSeriesVisibility,
	PerformanceMetricsTable,
	usePerformanceMetricsTable,
} from "./PerformanceMetricsTable";

// re-exported from ./common for backwards compatibility with existing import sites
export type { PerformancePanelRow, VisibleSeries };

/** Snapshot of the data backing the chart + metrics table, captured for an export of the current view. */
export type PerformanceExportSnapshot = {
	/** Current chart zoom (milliseconds, UTC) — feeds the export `dateRange`. */
	dateRange: { min: number; max: number };
	/** Visible metrics computed over the current zoom, keyed by metric then series uuid — the exact values the table shows. */
	computedMetrics: ComputedMetrics | undefined;
};

export type PerformancePanelHandle = {
	/** Live access to the underlying Highcharts wrapper. */
	readonly chart: HighchartsReactRefObject | null;
	/** Opens the AG Grid column chooser side panel. */
	openSettings(): void;
	/** Applies column state to the AG Grid (order + visibility). */
	applyColumnState(state: ColumnState[]): void;
	/** Returns the current zoom + computed metrics, so a caller can build a "what you see" export payload. */
	getExportSnapshot(): PerformanceExportSnapshot;
};

export type PerformancePanelProps = {
	rows: PerformancePanelRow[];
	/** Controlled per-series visibility, shared by the chart and the metrics table. Caller owns where it's stored. */
	seriesVisibility: ControlledSeriesVisibility;
	/** Column-state persistence handlers (remote- or session-backed) spread onto the metrics grid. */
	columnPersistence: ColumnPersistence;
	/** Prefix for the React Query keys used by the worker queries. */
	queryKeyPrefix: string;
	/** Column chooser side-panel title. */
	gridTitle: string;
	/** Column chooser side-panel subtitle. */
	gridSubtitle: string;
	importedPerformances?: InvestmentSummaryWithRequiredUuid["importedPerformances"];
	/** Fires whenever the worker-backed fetching state changes. */
	onFetchingChange?: (isFetching: boolean) => void;
};

export const PerformancePanel = React.forwardRef<PerformancePanelHandle, PerformancePanelProps>(
	function PerformancePanel(
		{
			rows,
			seriesVisibility,
			columnPersistence,
			queryKeyPrefix,
			gridTitle,
			gridSubtitle,
			importedPerformances,
			onFetchingChange,
		},
		ref,
	) {
		const workerApi = useDisposable(() => {
			return wrapTerminableWorker<PortfolioPerformanceWorkerApi>(new PortfolioPerformanceWorker());
		});

		const visibleSeries = seriesVisibility.value;

		const dateRangeWholeSeries = useMemo(() => computeDateRangeWholeSeries(rows), [rows]);

		const { dateRange, afterSetExtremes } = useHighchartsDiscretizedZoom(dateRangeWholeSeries, {
			resyncOnRangeChange: true,
		});

		const seriesInput = useMemo(
			() => Object.fromEntries(rows.map((row) => [row.uuid, { name: row.name, data: row.series }])),
			[rows],
		);

		const originalSeriesQuery = useQuery({
			queryKey: [queryKeyPrefix, "originalMs", workerId(workerApi), rows] as const,
			queryFn: () => workerApi.prepareOriginalInMsForChart(seriesInput),
			staleTime: Infinity,
			keepPreviousData: true,
		});

		const normalizedSeriesQuery = useQuery({
			queryKey: [queryKeyPrefix, "normalizedSeries", workerId(workerApi), rows, dateRange] as const,
			queryFn: () =>
				workerApi.prepareNormalizedForChart({ min: dateRange.min / 1000, max: dateRange.max / 1000 }, seriesInput),
			staleTime: Infinity,
			keepPreviousData: true,
			// was causing useMemo to preserve old series in the CustomizedChart component,
			// setting it to false guarantee the useMemo will see a new reference for cached values
			structuralSharing: false,
		});

		const metricsTable = usePerformanceMetricsTable({
			rows,
			dateRange,
			workerApi,
			seriesVisibility,
			grid: { columnPersistence, queryKeyPrefix, title: gridTitle, subtitle: gridSubtitle },
		});

		const visibleNormalizedSeries = useMemo(
			() =>
				Object.fromEntries(
					Object.entries(normalizedSeriesQuery.data ?? {}).filter(([uuid]) => visibleSeries?.[uuid] !== false),
				),
			[normalizedSeriesQuery.data, visibleSeries],
		);

		const visibleOriginalMs = useMemo(
			() =>
				Object.fromEntries(
					Object.entries(originalSeriesQuery.data ?? {}).filter(([uuid]) => visibleSeries?.[uuid] !== false),
				),
			[originalSeriesQuery.data, visibleSeries],
		);

		// Keyed by uuid, not name: the same portfolio can be loaded twice (Portfolio Lab), yielding
		// two rows with an identical `name`. A name-keyed map would collapse them and paint both
		// Highcharts lines the same color; the uuid is unique per row. Fall back to a name-derived
		// color only when no row matches the series key.
		const colorByUuid = useMemo(() => new Map(rows.map((r) => [r.uuid, r.color])), [rows]);
		const seriesColorFn = useCallback(
			(uuid: string, name: string) => colorByUuid.get(uuid) ?? stableColorGenerator(name),
			[colorByUuid],
		);

		const isFetching = metricsTable.isFetching || normalizedSeriesQuery.isFetching || originalSeriesQuery.isFetching;
		useEffect(() => {
			onFetchingChange?.(isFetching);
		}, [isFetching, onFetchingChange]);

		const chartRef = useRef<HighchartsReactRefObject | null>(null);
		useImperativeHandle(
			ref,
			() => ({
				get chart() {
					return chartRef.current;
				},
				openSettings: metricsTable.openSettings,
				applyColumnState: metricsTable.applyColumnState,
				getExportSnapshot: () => ({
					dateRange,
					computedMetrics: metricsTable.tableProps.context.computedMetrics,
				}),
			}),
			[
				metricsTable.openSettings,
				metricsTable.applyColumnState,
				metricsTable.tableProps.context.computedMetrics,
				dateRange,
			],
		);

		return (
			<Column flexGrow={0} gap={0}>
				<CustomizedChart
					ref={chartRef}
					normalizedSeries={visibleNormalizedSeries}
					originalMs={visibleOriginalMs}
					importedPerformances={importedPerformances}
					dateRangeWholeSeries={dateRangeWholeSeries}
					afterSetExtremes={afterSetExtremes}
					seriesColorFn={seriesColorFn}
				/>
				<PerformanceMetricsTable {...metricsTable.tableProps} />
			</Column>
		);
	},
);

export type CustomizedChartProps = {
	normalizedSeries: ReturnType<PortfolioPerformanceWorkerApi["prepareNormalizedForChart"]>;
	originalMs: ReturnType<PortfolioPerformanceWorkerApi["prepareOriginalInMsForChart"]>;
	importedPerformances?: PerformancePanelProps["importedPerformances"];
	dateRangeWholeSeries: { min: number; max: number };
	afterSetExtremes(
		this: Highcharts.Axis,
		event: {
			userMax?: number;
			userMin?: number;
		},
	): void;
	/** Override per-series color. Receives the series' uuid key; defaults to stableColorGenerator(name). */
	seriesColorFn?: (uuid: string, name: string) => string;
};

export const CustomizedChart = React.forwardRef<HighchartsReactRefObject, CustomizedChartProps>(
	function CustomizedChart(
		{ normalizedSeries, originalMs, importedPerformances, dateRangeWholeSeries, afterSetExtremes, seriesColorFn },
		ref,
	) {
		const { formatDate, formatNumber } = useLocaleFormatters();

		const chartRef = useRefProxy<HighchartsReactRefObject>(null, ref);
		const selectedCustomButtonRef = useRef<number | null>(8); // All

		const navigatorSeries = useMemo(
			() =>
				Object.entries(originalMs).map(([uuid, x]) => ({
					data: x.originalMs,
					color: seriesColorFn ? seriesColorFn(uuid, x.name) : stableColorGenerator(x.name),
					name: x.name,
					type: "line" as const,
				})),
			[originalMs, seriesColorFn],
		);

		const chartSeries = useMemo(
			() =>
				Object.entries(normalizedSeries).map(([uuid, x]) => {
					const color = seriesColorFn ? seriesColorFn(uuid, x.name) : stableColorGenerator(x.name);
					return {
						data: x.normalizedMs,
						color,
						marker: { fillColor: color },
						name: x.name,
						type: "line" as const,
					};
				}),
			[normalizedSeries, seriesColorFn],
		);

		const reapplyButtonState = useHighchartsReapplyButtonState(chartRef, selectedCustomButtonRef);

		const options = useMemo<Highcharts.Options>(
			() => ({
				chart: {
					animation: false,
					style: {
						fontFamily: "Gotham,sans-serif",
					},
					panning: {
						enabled: true,
					},
				},
				credits: {
					enabled: false,
				},
				legend: {
					enabled: false,
				},
				title: {
					text: undefined,
				},
				subtitle: {
					text: undefined,
				},
				time: { useUTC: true },
				xAxis: [
					{
						crosshair: true,
						type: "datetime",
						min: dateRangeWholeSeries.min,
						max: dateRangeWholeSeries.max,
						endOnTick: false,
						startOnTick: false,
						minTickInterval: Math.min(
							dateRangeWholeSeries.max + 86400000 - dateRangeWholeSeries.min,
							1000 * 60 * 60 * 24,
						),
						events: {
							afterSetExtremes(this: Highcharts.Axis, event: { userMax?: number; userMin?: number }) {
								if (event.userMax != null && event.userMin != null) {
									selectedCustomButtonRef.current = null;
								}
								afterSetExtremes.call(this, event);
							},
						},
						plotLines: importedPerformances?.lastAuditedDate
							? [
									{
										color: getThemeCssVars(themeCSSVars.palette_S200),
										width: 2,
										value: new Date(importedPerformances.lastAuditedDate).getTime(),
										zIndex: 5,
									},
								]
							: undefined,
					},
				],
				yAxis: {
					crosshair: true,
					opposite: false,
					gridLine: 1,
					gridLineColor: "#CCCCCC",
				},
				boost: {
					debug: {
						timeRendering: true,
					},
					useGPUTranslations: true,
					seriesThreshold: 1,
				},
				tooltip: {
					enabled: true,
					shared: false,
					useHTML: true,
					borderColor: "transparent",
					borderRadius: 5,
					backgroundColor: PaletteColors.WHITE,

					formatter(this) {
						const content = `<div style="min-width: 12.5rem">
							<div style="text-align: center;
								border-radius: 0.25rem;
								background-color: #eeeef1;
								color: #9699b3;
								font-family: Gotham, sans-serif;
								font-weight: bold;
								line-height: 1.25rem;
								padding: 0.2rem 0;">
								<div>${formatDate(this.x, { timezone: "UTC" })}</div>
							</div>
							<table style="width: 100%">
								<tbody>${(this.points || [])
									// Highest series value first. Compared as numbers: `formatNumber` renders for a
									// locale, and reading that back with `Number` is NaN wherever the locale groups
									// with `.` or decimalises with `,`.
									.sort((p1, p2) => (p2.point.y ?? 0) - (p1.point.y ?? 0))
									.map(
										(point) => `
										<tr>
											<td style="border-bottom: solid 1px transparent; padding: 5px 8px 0 0">
											<span style="display: flex; flex-direction: row; align-items: center">
												<span style="display: inline-block; width: 10px; height: 10px; margin-right: 6px; border-radius: 100px; background-color: ${
													point.color
												}">
											</span>
											${point.series.name}</span>
											</td>
											<td
												style="text-align: right; font-weight: 500; border-bottom: solid 1px transparent; padding: 5px 0 0 0; color: black"
											>
												${formatNumber(point.point.y, 2)}%
											</td>
										</tr>
									`,
									)
									.join("")}
								</tbody>
							</table>
						</div>`;
						return content;
					},
				},
				plotOptions: {
					series: {
						animation: false,
						showInNavigator: false,
						turboThreshold: 0,
					},
				},
				navigator: {
					height: 20,
					// series are set imperatively in a separate useEffect to avoid chart.update()
					// disrupting ongoing drag interactions when chartSeries changes
				},
				exporting: {
					enabled: false,
				},
				series: chartSeries,
				rangeSelector: {
					buttons: [
						{
							text: "1d",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: dateRangeWholeSeries.max - 86400000,
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 0;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "1w",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: dateRangeWholeSeries.max - 7 * 86400000,
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 1;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "1m",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - 1, latest.getUTCDate()),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 2;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "3m",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - 3, latest.getUTCDate()),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 3;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "QTD",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const quarterStartMonth = Math.floor(latest.getUTCMonth() / 3) * 3;
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear(), quarterStartMonth, 0),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 4;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "6m",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - 6, latest.getUTCDate()),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 5;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "1y",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear() - 1, latest.getUTCMonth(), latest.getUTCDate()),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 6;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "YTD",
							title: "YTD",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const latest = new Date(dateRangeWholeSeries.max);
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: Date.UTC(latest.getUTCFullYear(), 0, 0),
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 7;
									reapplyButtonState();
									return false;
								},
							},
						},
						{
							text: "All",
							events: {
								click() {
									const chart = chartRef.current?.chart;
									if (!chart) return false;
									const { min, max } = adjustDateRangeSkippingWeekends({
										min: dateRangeWholeSeries.min,
										max: dateRangeWholeSeries.max,
									});
									chart.xAxis[0].setExtremes(min, max, true);
									selectedCustomButtonRef.current = 8;
									reapplyButtonState();
									return false;
								},
							},
						},
					],
				},
			}),
			[
				afterSetExtremes,
				formatDate,
				formatNumber,
				importedPerformances,
				dateRangeWholeSeries.max,
				dateRangeWholeSeries.min,
				chartSeries,
				reapplyButtonState,
			],
		);

		useEffect(() => {
			chartRef.current?.chart.update({ navigator: { series: navigatorSeries } }, true);
		}, [navigatorSeries]);

		return (
			<HighchartsReact
				ref={chartRef}
				containerProps={{ style: { height: 300 + 60, minHeight: 300 + 60 } }}
				highcharts={Highcharts}
				constructorType="stockChart"
				options={options}
			/>
		);
	},
);
